import React, { useEffect } from "react";
import { createPortal } from "react-dom";

const Toast = ({
  message,
  type = "success",
  title,
  onClose,
  duration = 15000,
  hideTitle = false,
  className = "",
  portal = true,
  actionLabel,
  onAction,
}) => {
  useEffect(() => {
    if (message && duration) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [message, duration, onClose]);

  if (!message) return null;

  const typeStyles = {
    success: {
      bgColor: "bg-green-100",
      iconColor: "text-green-600",
      borderColor: "border-green-200",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      ),
    },
    error: {
      bgColor: "bg-red-100",
      iconColor: "text-red-600",
      borderColor: "border-red-200",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      ),
    },
    info: {
      bgColor: "bg-blue-100",
      iconColor: "text-blue-600",
      borderColor: "border-blue-200",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    warning: {
      bgColor: "bg-yellow-100",
      iconColor: "text-yellow-600",
      borderColor: "border-yellow-300",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01M10.29 3.86l-7.5 13A1 1 0 003.67 18h16.66a1 1 0 00.88-1.5l-7.5-13a1 1 0 00-1.76 0z"
          />
        </svg>
      ),
    },
  };

  const styles = typeStyles[type] || typeStyles.success;

  const toastNode = (
    <div
      className={`fixed top-4 right-4 z-[10000] animate-slideIn ${className}`.trim()}
      role="status"
      aria-live="polite"
    >
      <div
        className={`bg-white rounded-lg shadow-lg border ${styles.borderColor} p-4 min-w-[320px] max-w-md`}
      >
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div
              className={`w-8 h-8 ${styles.bgColor} rounded-full flex items-center justify-center`}
            >
              <div className={styles.iconColor}>{styles.icon}</div>
            </div>
          </div>

          {/* Message Content */}
          <div className="flex-1 pt-0.5">
            {!hideTitle && (
              <p className="text-sm font-semibold text-gray-900 mb-1">
                {/* {type.charAt(0).toUpperCase() + type.slice(1)} */}
                {title || {
                success: "Success",
                warning: "Warning",
                error: "Error",
                info: "Information",
              }[type]
}
              </p>
            )}
            <p className="text-sm text-gray-600 whitespace-pre-line">{message}</p>
            {actionLabel && typeof onAction === "function" ? (
              <button
                type="button"
                onClick={() => {
                  onAction();
                  if (typeof onClose === "function") onClose();
                }}
                className="mt-2 text-sm font-semibold text-[var(--accent,#a51c1c)] hover:underline"
              >
                {actionLabel}
              </button>
            ) : null}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss notification"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  if (!portal || typeof document === "undefined") {
    return toastNode;
  }

  return createPortal(toastNode, document.body);
};

export default Toast;
